import React, { FC, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ResultReviewData } from '../apis/prescription'

interface ResultReviewPanelProps {
  data: ResultReviewData | null
  isLoading: boolean
  error: string
}

const SectionTitle: FC<{ title: string }> = ({ title }) => (
  <Typography
    variant="subtitle1"
    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
  >
    {title}
  </Typography>
)

// 医療機関系キーの判定
const isHospitalKey = (key: string): boolean =>
  key.startsWith('id_1_') || key.startsWith('id_4_') || key.startsWith('id_5_')

export const ResultReviewPanel: FC<ResultReviewPanelProps> = ({
  data,
  isLoading,
  error,
}) => {
  const [showOtherFields, setShowOtherFields] = useState(false)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          request_idを入力してください
        </Typography>
      </Box>
    )
  }

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      {/* 初期診断データ */}
      <SectionTitle title="初期診断データ" />

      {/* basic_result 主要項目 */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: '40%' }}>項目</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>値</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.basic_result.main_fields.map((field) => (
              <TableRow
                key={field.key}
                sx={{
                  bgcolor: isHospitalKey(field.key) ? '#e6f3ff' : '#fff9e6',
                }}
              >
                <TableCell>{field.display_name}</TableCell>
                <TableCell>{field.value || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* basic_result その他項目 */}
      {data.basic_result.other_fields.length > 0 && (
        <Accordion
          expanded={showOtherFields}
          onChange={() => setShowOtherFields(!showOtherFields)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" color="text.secondary">
              その他の項目（{data.basic_result.other_fields.length}件）
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                {data.basic_result.other_fields.map((field) => (
                  <TableRow key={field.key}>
                    <TableCell sx={{ width: '40%', color: 'text.secondary' }}>
                      {field.key}
                    </TableCell>
                    <TableCell>{field.value || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {/* med_result */}
      {data.med_result.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            薬剤情報
          </Typography>
          {data.med_result.map((rp, idx) => (
            <Accordion key={idx} defaultExpanded={idx === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rp{rp.rp_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rp.usage}
                  </Typography>
                  {rp.dispensing_quantity && (
                    <Typography variant="body2" color="text.secondary">
                      {rp.dispensing_quantity}日分
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {rp.comment && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: 'info.main' }}
                  >
                    {rp.comment}
                  </Typography>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>薬品名称</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '80px' }}>
                        用量
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '60px' }}>
                        単位
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rp.medications.map((med, medIdx) => (
                      <TableRow key={medIdx}>
                        <TableCell>{med.name || '-'}</TableCell>
                        <TableCell>{med.dosage || '-'}</TableCell>
                        <TableCell>{med.unit || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ユーザ報告 */}
      <SectionTitle title="ユーザ報告" />

      {data.ocr_errors.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          ユーザ報告なし
        </Typography>
      )}

      {/* OCRError */}
      {data.ocr_errors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            OCRエラー報告（{data.ocr_errors.length}件）
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#ffe6e6' }}>
                  <TableCell sx={{ fontWeight: 600 }}>日時</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ラベル</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.ocr_errors.map((err) => (
                  <TableRow key={err.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(err.created_at).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell>{err.label}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  )
}
